/* CustomizeCatalogPanel — the "Customize catalog" quick slide-in for sellers.
 *
 * Always-available from /seller/catalog. Three tabs, each saving to the
 * existing backend (no wizard, no restart):
 *   Layout     — default catalog view (grid / list / table)
 *   Fields     — which fields buyers see, with a live preview
 *   Visibility — Catalog app on/off + Public storefront on/off
 *
 * Field visibility persists via /seller/catalog-import/visibility and drives
 * the in-portal catalog cards (vis prop on CatalogProductCard). */
import { useEffect, useMemo, useState } from "react";
import api, { money, formatErr } from "../../lib/api";
import {
  Sheet, SheetContent, SheetCloseX, SheetHeader, SheetTitle, SheetFooter,
} from "../ui/sheet";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Sliders, LayoutGrid, List, Table2, ListChecks, Eye, Store, ShoppingBag,
  Package, Save, Loader2, Wand2, ShieldAlert,
} from "lucide-react";

const FIELD_LABELS = {
  image: "Product image", sku: "SKU", category: "Category", brand: "Brand",
  description: "Description", retail_price: "Retail / MSRP", map_price: "MAP price",
  moq: "MOQ", stock: "Stock level", badge: "Badges (new / top seller)",
  fulfillment: "Fulfillment type", case_size: "Case size", upc: "UPC", tags: "Tags",
  sub_category: "Sub-category", variant: "Variant", size: "Size",
  product_type: "Product type", gender: "Gender", ean: "EAN", asin: "Amazon ASIN",
  condition: "Condition (Clean/Decoded)", exp_date: "Expiration date",
};
// Always-on fields the seller can't hide (the catalog needs them).
const LOCKED = new Set(["name", "price", "gallery_images"]);
const LAYOUTS = [
  { key: "grid", label: "Grid", desc: "Image-led cards", icon: LayoutGrid },
  { key: "list", label: "List", desc: "Compact rows", icon: List },
  { key: "text", label: "Table", desc: "Dense linesheet", icon: Table2 },
];

export default function CustomizeCatalogPanel({
  open, onOpenChange, viewMode, setViewMode,
  catalogFeature, onToggleCatalogApp, sampleProduct, onVisibilityChange,
}) {
  const [tab, setTab] = useState("layout");
  const [fields, setFields] = useState(null);
  const [canonical, setCanonical] = useState([]);
  const [storefrontPublic, setStorefrontPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/seller/catalog-import/visibility")
      .then(r => { setFields(r.data.fields || {}); setCanonical(r.data.canonical_fields || []); })
      .catch(() => { setFields({}); });
    api.get("/tenant/me")
      .then(r => setStorefrontPublic((r.data?.settings?.storefront_public) !== false))
      .catch(() => { /* ignore */ });
  }, [open]);

  const toggleField = (k) => setFields(f => ({ ...f, [k]: f[k] === false ? true : false }));
  const flip = toggleField;

  const orderedFields = useMemo(() => {
    const base = (canonical.length ? canonical : Object.keys(FIELD_LABELS))
      .filter(k => !LOCKED.has(k) && FIELD_LABELS[k]);
    return base;
  }, [canonical]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/seller/catalog-import/visibility", { fields });
      await api.patch("/tenant/me", { settings: { storefront_public: storefrontPublic } });
      onVisibilityChange?.(fields);
      toast.success("Catalog updated");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const show = (k) => !fields || fields[k] !== false;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="customize-catalog-panel">
        <SheetCloseX/>
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2"><Sliders className="w-4 h-4" />Customize catalog</SheetTitle>
          <div className="flex gap-1 mt-3">
            {[["layout", "Layout", LayoutGrid], ["fields", "Fields", ListChecks], ["visibility", "Visibility", Eye]].map(([k, l, I]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`customize-tab-${k}`}
                className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[12.5px] transition-colors ${tab === k ? "bg-foreground text-background" : "bg-secondary text-foreground/65 hover:text-foreground"}`}>
                <I className="w-3.5 h-3.5" />{l}
              </button>
            ))}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {!fields ? (
            <div className="grid place-items-center py-16"><Loader2 className="w-5 h-5 animate-spin text-foreground/40" /></div>
          ) : (
            <>
              {tab === "layout" && (
                <div>
                  <p className="text-[13px] text-foreground/60 mb-4">How products are laid out in your catalog. Changes apply instantly.</p>
                  <div className="grid grid-cols-3 gap-3">
                    {LAYOUTS.map(({ key, label, desc, icon: I }) => (
                      <button key={key} onClick={() => setViewMode(key)} data-testid={`customize-layout-${key}`}
                        className={`rounded-xl border p-4 text-left transition-colors ${viewMode === key ? "border-foreground bg-foreground text-background" : "border-border bg-white hover:border-foreground/40"}`}>
                        <I className="w-5 h-5" />
                        <div className="font-medium text-sm mt-3">{label}</div>
                        <div className={`text-[11.5px] mt-0.5 ${viewMode === key ? "text-background/70" : "text-foreground/55"}`}>{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {tab === "fields" && (
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-0.5">
                    <p className="text-[13px] text-foreground/60 mb-2">Toggle what appears on your product cards.</p>
                    {orderedFields.map(k => (
                      <div key={k} className="flex items-center justify-between py-1.5">
                        <span className="text-[13px]">{FIELD_LABELS[k]}</span>
                        <Switch checked={fields[k] !== false} onCheckedChange={() => flip(k)} data-testid={`customize-field-${k}`} />
                      </div>
                    ))}
                    <div className="text-[11px] text-foreground/45 pt-2">Name &amp; wholesale price always show.</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45 mb-2">Live preview · as buyers see it</div>
                    <PreviewCard p={sampleProduct} show={show} />
                  </div>
                </div>
              )}

              {tab === "visibility" && (
                <div className="space-y-3">
                  <p className="text-[13px] text-foreground/60">Control where your catalog is visible.</p>
                  <Row icon={Store} title="Catalog app"
                    desc="Connected buyers can browse your catalog inside their verqo portal."
                    checked={!!catalogFeature?.enabled} disabled={!catalogFeature?.feature_enabled}
                    onChange={onToggleCatalogApp} testid="customize-catalog-app" />
                  <Row icon={ShoppingBag} title="Public storefront"
                    desc="Anyone with your /s/ link can browse & order. Guests sign up at checkout."
                    checked={storefrontPublic} onChange={() => setStorefrontPublic(v => !v)} testid="customize-storefront" />
                  <a href="/seller/catalog/setup" data-testid="customize-open-wizard"
                    className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-foreground/60 hover:text-foreground">
                    <Wand2 className="w-3.5 h-3.5" />Need industry presets or custom fields? Open the full setup wizard →
                  </a>
                </div>
              )}
            </>
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border">
          <Button onClick={save} disabled={saving || !fields} data-testid="customize-save"
            className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Row({ icon: Icon, title, desc, checked, disabled, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-white p-4">
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 text-foreground/55 mt-0.5" />
        <div>
          <div className="font-medium text-sm">{title}</div>
          <div className="text-[12px] text-foreground/55 mt-0.5">{desc}</div>
        </div>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function PreviewCard({ p, show }) {
  const sample = p || {
    name: "Sample product", sku: "SKU-001", category: "Catalog", price: 12.5,
    retail_price: 24, map_price: 20, moq: 12, stock: 120, image: null,
  };
  const margin = sample.retail_price ? Math.round((1 - sample.price / sample.retail_price) * 100) : null;
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden max-w-[240px]">
      {show("image") && (
        <div className="aspect-[4/3] bg-secondary overflow-hidden grid place-items-center">
          {sample.image
            ? <img src={sample.image} alt={sample.name} className="w-full h-full object-cover" />
            : <Package className="w-8 h-8 text-foreground/25" />}
        </div>
      )}
      <div className="p-3">
        {show("category") && <div className="text-[10px] mono uppercase tracking-wide text-foreground/45">{sample.category}</div>}
        <div className="font-medium text-[13px] mt-0.5 leading-snug">{sample.name}</div>
        {show("brand") && sample.brand && <div className="text-[11px] text-foreground/50 mt-0.5">{sample.brand}</div>}
        {show("sku") && <div className="text-[11px] mono text-foreground/40 mt-0.5">{sample.sku}</div>}
        {show("description") && sample.description && <div className="text-[11px] text-foreground/55 mt-1 line-clamp-2">{sample.description}</div>}
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="display text-lg">{money(sample.price)}</div>
            {show("moq") && <div className="text-[10px] text-foreground/45 mono">MOQ {sample.moq}</div>}
          </div>
          <div className="text-right">
            {show("retail_price") && sample.retail_price && <div className="text-[10px] text-foreground/45">Retail <span className="line-through">{money(sample.retail_price)}</span>{margin && <span className="text-emerald-700"> · {margin}%</span>}</div>}
            {show("map_price") && sample.map_price != null && <div className="text-[10px] text-amber-700 inline-flex items-center gap-0.5"><ShieldAlert className="w-2.5 h-2.5" />MAP {money(sample.map_price)}</div>}
            {show("stock") && <div className="text-[10px] text-emerald-600">{sample.stock} in stock</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
